import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { LogOut, User, Sun, Moon, Settings, Gift } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <nav
      data-testid="navbar"
      className="print-hide sticky top-0 z-50 w-full border-b-2 border-[var(--c-border)] bg-[var(--c-bg-95)] backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border-2 border-[var(--c-border)] bg-white shadow-[3px_3px_0px_var(--c-shadow)]">
            <img src="/logo-mark.png" alt="Texas LLC Guide" className="h-8 w-8 object-contain" data-testid="navbar-logo" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-black leading-tight tracking-tight text-[var(--c-text)] sm:text-xl">
              Texas LLC Guide
            </h1>
            <p className="hidden text-xs text-[var(--c-muted)] sm:block">
              Your step-by-step starter bundle
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Dark mode toggle */}
          <button
            data-testid="dark-mode-toggle"
            onClick={toggle}
            className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)]"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-[var(--c-text)]" />}
          </button>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2 font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_var(--c-shadow)]"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden text-sm sm:inline">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[4px_4px_0px_var(--c-shadow)]"
              >
                <DropdownMenuItem className="text-sm text-[var(--c-muted)] focus:bg-transparent cursor-default">
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="referrals-link"
                  onClick={() => navigate("/referrals")}
                  className="cursor-pointer font-semibold text-[var(--c-text)]"
                >
                  <Gift className="mr-2 h-4 w-4 text-[#1E3A8A]" />
                  Refer & Earn
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="admin-panel-link"
                      onClick={() => navigate("/admin")}
                      className="cursor-pointer font-semibold text-[#1E3A8A] focus:text-[#1E3A8A]"
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Customers
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  data-testid="logout-button"
                  onClick={logout}
                  className="cursor-pointer font-semibold text-[#FF4D4D] focus:text-[#FF4D4D]"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </nav>
  );
}
